import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { CheckCircleIcon, XIcon, RefreshCwIcon, ArrowRightIcon, HeadphonesIcon } from 'lucide-react';
const QUIZ_QUESTIONS = [{
  id: 1,
  type: 'multiple-choice',
  question: {
    en: 'What number comes after twenty-three?',
    ar: 'ما هو العدد الذي يأتي بعد ثلاثة وعشرين؟'
  },
  options: ['22', '24', '25', '21'],
  correctAnswer: '24',
  explanation: {
    en: 'Twenty-four (24) comes after twenty-three (23) in numerical order.',
    ar: 'أربعة وعشرون (24) يأتي بعد ثلاثة وعشرين (23) في الترتيب العددي.'
  }
}, {
  id: 2,
  type: 'number-sequence',
  question: {
    en: 'Complete the sequence: 5, 10, 15, __, 25',
    ar: 'أكمل التسلسل: 5, 10, 15, __, 25'
  },
  correctAnswer: '20',
  explanation: {
    en: 'The numbers are increasing by 5 each time.',
    ar: 'الأرقام تزداد بمقدار 5 في كل مرة.'
  }
}, {
  id: 3,
  type: 'multiple-choice',
  question: {
    en: 'Which is the correct way to write 45 in words?',
    ar: 'ما هي الطريقة الصحيحة لكتابة 45 بالكلمات؟'
  },
  options: ['fourty five', 'forty-five', 'fourty-five', 'forty five'],
  correctAnswer: 'forty-five',
  explanation: {
    en: 'Forty-five is the correct spelling, with a hyphen between the words.',
    ar: 'forty-five هي التهجئة الصحيحة، مع وضع شرطة بين الكلمتين.'
  }
}];
export function CountingQuiz() {
  const {
    isArabic
  } = useLanguage();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const playNumber = (text: string) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'en-US';
      window.speechSynthesis.speak(utterance);
    }
  };
  const handleAnswerSelect = (answer: string) => {
    if (showResult) return;
    setSelectedAnswer(answer);
  };
  const checkAnswer = () => {
    if (!selectedAnswer) return;
    setShowResult(true);
    if (selectedAnswer === QUIZ_QUESTIONS[currentQuestion].correctAnswer) {
      setScore(score + 1);
    }
  };
  const nextQuestion = () => {
    if (currentQuestion < QUIZ_QUESTIONS.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setShowResult(false);
    } else {
      setIsComplete(true);
    }
  };
  const resetQuiz = () => {
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setScore(0);
    setIsComplete(false);
  };
  if (isComplete) {
    return <div className="text-center" data-id="element-224">
        <div className="mb-6" data-id="element-225">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-blue-100 dark:bg-blue-900/30 mb-4" data-id="element-226">
            <CheckCircleIcon className="w-10 h-10 text-blue-600 dark:text-blue-400" data-id="element-227" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-228">
            {isArabic ? 'أحسنت!' : 'Great job!'}
          </h3>
          <p className="text-gray-600 dark:text-gray-300" data-id="element-229">
            {isArabic ? `لقد حصلت على ${score} من أصل ${QUIZ_QUESTIONS.length}` : `You got ${score} out of ${QUIZ_QUESTIONS.length} correct`}
          </p>
        </div>
        <button onClick={resetQuiz} className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors" data-id="element-230">
          <RefreshCwIcon className="w-5 h-5" data-id="element-231" />
          <span data-id="element-232">{isArabic ? 'حاول مرة أخرى' : 'Try Again'}</span>
        </button>
      </div>;
  }
  const question = QUIZ_QUESTIONS[currentQuestion];
  return <div className="space-y-8" data-id="element-233">
      <div className="flex items-center justify-between" data-id="element-234">
        <div className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-235">
          {isArabic ? `السؤال ${currentQuestion + 1} من ${QUIZ_QUESTIONS.length}` : `Question ${currentQuestion + 1} of ${QUIZ_QUESTIONS.length}`}
        </div>
        <div className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-236">
          {isArabic ? 'النتيجة:' : 'Score:'} {score}
        </div>
      </div>
      <div data-id="element-237">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6" data-id="element-238">
          {isArabic ? question.question.ar : question.question.en}
        </h3>
        {'options' in question && <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-239">
            {question.options.map((option, index) => <button key={index} onClick={() => handleAnswerSelect(option)} disabled={showResult} className={`
                  p-4 rounded-lg text-left transition-all relative
                  ${selectedAnswer === option ? showResult ? option === question.correctAnswer ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'}
                `} data-id="element-240">
                <div className="flex items-center justify-between" data-id="element-241">
                  <span className="font-medium" data-id="element-242">{option}</span>
                  <button onClick={e => {
              e.stopPropagation();
              playNumber(option);
            }} className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 rounded-full transition-colors" data-id="element-243">
                    <HeadphonesIcon className="w-4 h-4" data-id="element-244" />
                  </button>
                </div>
                {showResult && selectedAnswer === option && <div className="absolute right-4 top-1/2 -translate-y-1/2" data-id="element-245">
                    {option === question.correctAnswer ? <CheckCircleIcon className="w-5 h-5 text-green-500 dark:text-green-400" data-id="element-246" /> : <XIcon className="w-5 h-5 text-red-500 dark:text-red-400" data-id="element-247" />}
                  </div>}
              </button>)}
          </div>}
        {question.type === 'number-sequence' && <div className="flex items-center gap-4 mb-4" data-id="element-248">
            <input type="text" value={selectedAnswer || ''} onChange={e => handleAnswerSelect(e.target.value)} disabled={showResult} className="w-24 px-4 py-2 text-center border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent" placeholder="?" data-id="element-249" />
          </div>}
        {showResult && <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg" data-id="element-250">
            <p className="text-gray-800 dark:text-gray-200" data-id="element-251">
              {isArabic ? question.explanation.ar : question.explanation.en}
            </p>
          </div>}
        <div className="mt-8 flex justify-end" data-id="element-252">
          {!showResult ? <button onClick={checkAnswer} disabled={!selectedAnswer} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-253">
              {isArabic ? 'تحقق' : 'Check'}
            </button> : <button onClick={nextQuestion} className="inline-flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-254">
              <span data-id="element-255">{isArabic ? 'التالي' : 'Next'}</span>
              <ArrowRightIcon className="w-4 h-4" data-id="element-256" />
            </button>}
        </div>
      </div>
    </div>;
}